/**
 * Simple Google Maps overlay that renders the given content at the given
 * position, very similar to the built-in marker.
 *
 * The reason we use this overlay instead of the built-in marker is that it's
 * very limited and only allows using images and very basic SVG paths. This
 * overlay allow us to fully utilize HTML, CSS and React to create
 * interactive markers.
 *
 * Because of the nature of the Google Maps API (which is loaded dynamically
 * at runtime instead of being bundled in our app), the definition of the
 * class is hidden behind this function, since the class it extends from might
 * not be available in time if we defined the class at the top level.
 */
export const makeElementMarkerOverlayClass = () => {
  class ElementMarkerOverlay extends google.maps.OverlayView {
    position: google.maps.LatLng | google.maps.LatLngLiteral
    container: HTMLElement
    content: HTMLElement

    constructor(
      content: HTMLElement,
      position: google.maps.LatLng | google.maps.LatLngLiteral,
      anchorPosition: 'center' | 'bottom' = 'bottom'
    ) {
      super()
      this.position = position
      this.content = content

      const popup = document.createElement('div')
      // TODO: This attribute does exist but our TypeScript version or
      // environment don't know about it
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      popup.role = 'button'
      popup.tabIndex = 0
      popup.classList.add(
        'absolute',
        'top-0',
        'left-0',
        'transform',
        '-translate-x-[50%]',
        anchorPosition === 'bottom' ? '-translate-y-[100%]' : '-translate-x-[50%]'
      )
      popup.appendChild(content)

      const anchor = document.createElement('div')
      anchor.classList.add('absolute', 'w-full', 'bottom-0', 'left-0')
      anchor.appendChild(popup)

      this.container = document.createElement('div')
      this.container.classList.add('absolute', 'h-0', 'w-min', 'cursor-pointer', 'z-[0]')
      this.container.appendChild(anchor)
      ElementMarkerOverlay.preventMapHitsFrom(this.container)
    }

    onAdd() {
      this.getPanes()?.floatPane.appendChild(this.container)
    }

    onRemove() {
      if (this.container.parentElement) {
        this.container.parentElement.removeChild(this.container)
      }
    }

    draw() {
      const position = this.getProjection().fromLatLngToDivPixel(this.position)
      if (!position) {
        return
      }

      const display = Math.abs(position.x) < 4000 && Math.abs(position.y) < 4000 ? 'block' : 'none'

      if (display === 'block') {
        this.container.style.left = position.x + 'px'
        this.container.style.top = position.y + 'px'
      }

      if (this.container.style.display !== display) {
        this.container.style.display = display
      }
    }
  }

  return ElementMarkerOverlay
}
